import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, StateToken, createSelector } from '@ngxs/store';
import { switchMap, tap } from 'rxjs';
import { RelatedToType } from '@shared/enums';
import { ApprovalStatus } from '@shared/modules/approvals/approvals.types';
import { StateBase } from '@state/state-base';
import { ApprovalProcess, ApprovalsStateModel, EligibleApprover, EligibleApproverGroup } from '../approvals.types';
import { ApprovalsService } from '../approvals.service';
import { Approvals } from './actions';

const initialState: ApprovalsStateModel = {
  approvals: [],
  eligibleApprovers: [],
  eligibleApproverGroups: [],
  loading: false,
  activeApprovalId: null,
  relatedToId: null,
  relatedToType: null,
};

@State<ApprovalsStateModel>({
  name: new StateToken<ApprovalsStateModel>('approvalsState'),
  defaults: initialState,
})
@Injectable()
export class ApprovalsState extends StateBase {
  constructor(private readonly approvalsService: ApprovalsService) {
    super();
  }

  @Selector()
  static approvals(state: ApprovalsStateModel): ApprovalProcess[] {
    return state.approvals;
  }

  @Selector()
  static loading(state: ApprovalsStateModel): boolean {
    return state.loading;
  }

  @Selector()
  static activeApprovalId(state: ApprovalsStateModel): number | null {
    return state.activeApprovalId;
  }

  @Selector()
  static activeApproval(state: ApprovalsStateModel): ApprovalProcess | null {
    return state.approvals.find((a) => a.id === state.activeApprovalId) ?? null;
  }

  @Selector()
  static activeApprovalSnapshot(state: ApprovalsStateModel): Record<string, any> {
    return state.approvals.find((a) => a.id === state.activeApprovalId)?.snapshot ?? {};
  }

  @Selector()
  static eligibleApprovers(state: ApprovalsStateModel): EligibleApprover[] {
    return state.eligibleApprovers;
  }

  @Selector()
  static eligibleApproverGroups(state: ApprovalsStateModel): EligibleApproverGroup[] {
    return state.eligibleApproverGroups;
  }

  @Selector()
  static relatedToId(state: ApprovalsStateModel): number | null {
    return state.relatedToId;
  }

  @Selector()
  static relatedToType(state: ApprovalsStateModel): RelatedToType | null {
    return state.relatedToType;
  }

  static approvalById(id: number) {
    return createSelector(
      [ApprovalsState],
      (state: ApprovalsStateModel) => state.approvals.find((a) => a.id === id) ?? null,
    );
  }

  @Action(Approvals.SetRelatedTo)
  setRelatedTo(ctx: StateContext<ApprovalsStateModel>, { relatedToId, relatedToType }: Approvals.SetRelatedTo) {
    ctx.patchState({ relatedToId, relatedToType });
  }

  @Action(Approvals.LoadEligibleApprovers)
  loadEligibleApprovers(ctx: StateContext<ApprovalsStateModel>) {
    const { relatedToId, relatedToType } = ctx.getState();
    return this.approvalsService.getEligibleApprovers(relatedToId!, relatedToType!).pipe(
      tap(({ eligible_approvers, eligible_approver_groups }) =>
        ctx.patchState({
          eligibleApprovers: eligible_approvers,
          eligibleApproverGroups: eligible_approver_groups,
        }),
      ),
    );
  }

  @Action(Approvals.LoadApprovals)
  loadApprovals(ctx: StateContext<ApprovalsStateModel>) {
    const { relatedToId, relatedToType } = ctx.getState();
    ctx.patchState({ loading: true });
    return this.approvalsService
      .getApprovals(relatedToId!, relatedToType!)
      .pipe(tap((approvals) => ctx.patchState({ approvals, loading: false })));
  }

  @Action(Approvals.AddApproval)
  addApproval(ctx: StateContext<ApprovalsStateModel>, { name, layout, snapshot }: Approvals.AddApproval) {
    const { relatedToId, relatedToType } = ctx.getState();
    return this.approvalsService
      .createApproval(name, layout, relatedToId!, relatedToType!, snapshot)
      .pipe(tap((approval) => ctx.patchState({ approvals: [...ctx.getState().approvals, approval] })));
  }

  @Action(Approvals.DeleteApproval)
  deleteApproval(ctx: StateContext<ApprovalsStateModel>, { id }: Approvals.DeleteApproval) {
    const { relatedToId, relatedToType } = ctx.getState();
    return this.approvalsService
      .deleteApproval(id, relatedToId!, relatedToType!)
      .pipe(tap(() => ctx.patchState({ approvals: ctx.getState().approvals.filter((a) => a.id !== id) })));
  }

  @Action(Approvals.SetActiveApproval)
  setActiveApproval(ctx: StateContext<ApprovalsStateModel>, { approvalId }: Approvals.SetActiveApproval) {
    ctx.patchState({ activeApprovalId: approvalId });
  }

  @Action(Approvals.UpdateLayout)
  updateLayout(ctx: StateContext<ApprovalsStateModel>, { approvalId, layout }: Approvals.UpdateLayout) {
    const { relatedToId, relatedToType } = ctx.getState();
    return this.approvalsService.updateLayout(approvalId, layout, relatedToId!, relatedToType!).pipe(
      tap((updated) =>
        ctx.patchState({
          approvals: ctx.getState().approvals.map((a) => (a.id === approvalId ? updated : a)),
        }),
      ),
    );
  }

  @Action(Approvals.SendApprovalRequest)
  sendApprovalRequest(
    ctx: StateContext<ApprovalsStateModel>,
    { approvalId, approvers }: Approvals.SendApprovalRequest,
  ) {
    const { relatedToId, relatedToType } = ctx.getState();
    return this.approvalsService.sendApprovalRequest(approvalId, approvers, relatedToId!, relatedToType!).pipe(
      switchMap(() => this.approvalsService.getApprovalById(approvalId, relatedToId!, relatedToType!)),
      tap((updated) =>
        ctx.patchState({
          approvals: ctx.getState().approvals.map((a) => (a.id === approvalId ? updated : a)),
        }),
      ),
    );
  }

  @Action(Approvals.UpdateSnapshot)
  updateSnapshot(ctx: StateContext<ApprovalsStateModel>, { approvalId, snapshot }: Approvals.UpdateSnapshot) {
    const { relatedToId, relatedToType } = ctx.getState();
    return this.approvalsService.updateSnapshot(approvalId, snapshot, relatedToId!, relatedToType!).pipe(
      tap((updated) =>
        ctx.patchState({
          approvals: ctx.getState().approvals.map((a) => (a.id === approvalId ? updated : a)),
        }),
      ),
    );
  }

  @Action(Approvals.UpdateName)
  updateName(ctx: StateContext<ApprovalsStateModel>, { approvalId, name }: Approvals.UpdateName) {
    const { relatedToId, relatedToType } = ctx.getState();
    return this.approvalsService.updateName(approvalId, name, relatedToId!, relatedToType!).pipe(
      tap((updated) =>
        ctx.patchState({
          approvals: ctx.getState().approvals.map((a) => (a.id === approvalId ? updated : a)),
        }),
      ),
    );
  }

  @Action(Approvals.RefreshApproval)
  refreshApproval(ctx: StateContext<ApprovalsStateModel>, { id }: Approvals.RefreshApproval) {
    const { relatedToId, relatedToType } = ctx.getState();
    return this.approvalsService.getApprovalById(id, relatedToId!, relatedToType!).pipe(
      tap((updated) =>
        ctx.patchState({
          approvals: ctx.getState().approvals.map((a) => (a.id === id ? updated : a)),
        }),
      ),
    );
  }

  @Action(Approvals.Reset)
  reset(ctx: StateContext<ApprovalsStateModel>) {
    ctx.setState(initialState);
  }

  @Action(Approvals.SubmitReview)
  submitReview(ctx: StateContext<ApprovalsStateModel>, { approvalRowId, status, comments }: Approvals.SubmitReview) {
    const activeApprovalId = ctx.getState().activeApprovalId;
    const request$ =
      status === ApprovalStatus.APPROVED
        ? this.approvalsService.approveReview(approvalRowId, comments)
        : this.approvalsService.declineReview(approvalRowId, comments);

    if (!activeApprovalId) return request$;

    const { relatedToId, relatedToType } = ctx.getState();
    return request$.pipe(
      switchMap(() => this.approvalsService.getApprovalById(activeApprovalId, relatedToId!, relatedToType!)),
      tap((updated) =>
        ctx.patchState({
          approvals: ctx.getState().approvals.map((a) => (a.id === activeApprovalId ? updated : a)),
        }),
      ),
    );
  }
}
